import axiosInstance from "./axiosInstance";

/**
 * Fetches a paginated list of users from the API.
 * @param {object} params - The query parameters for the request.
 * @param {number} params.page - The page number to fetch.
 * @param {number} params.pageSize - The number of users per page.
 * @param {string} [params.username] - An optional username to filter by.
 * @returns {Promise} The promise from the axios GET request.
 */
export const getUsers = (params) => {
  const queryParams = { ...params, site: process.env.REACT_APP_SITE_ID };
  return axiosInstance.get("/users", { params: queryParams });
};

/**
 * Creates a new user.
 * @param {object} userData - The data for the new user.
 * @param {string} userData.username - The username.
 * @param {string} userData.password - The password.
 * @returns {Promise} The promise from the axios POST request.
 */
export const createUser = (userData) => {
  const payload = { ...userData, site: process.env.REACT_APP_SITE_ID };
  return axiosInstance.post("/users", payload);
};

/**
 * Updates the role of a specific user.
 * @param {string} userId - The ID of the user to update.
 * @param {string} role - The new role to assign.
 * @returns {Promise} The promise from the axios PATCH request.
 */
export const updateUserRole = (userId, role) => {
  return axiosInstance.patch(
    `/users/update-role`,
    {},
    {
      params: {
        id: userId,
        role: role,
        site: process.env.REACT_APP_SITE_ID,
      },
    }
  );
};

/**
 * Toggles the status of a specific user.
 * @param {string} userId - The ID of the user to update.
 * @returns {Promise} The promise from the axios PATCH request.
 */
export const updateUserStatus = (userId) => {
  return axiosInstance.patch(`/users/update-status/${userId}`, {
    site: process.env.REACT_APP_SITE_ID,
  });
};

/**
 * Deletes a specific user.
 * @param {string} userId - The ID of the user to delete.
 * @returns {Promise} The promise from the axios DELETE request.
 */
export const deleteUser = (userId) => {
  return axiosInstance.delete(`/users/${userId}`, {
    params: { site: process.env.REACT_APP_SITE_ID },
  });
};

/**
 * Updates the password for a specific user (admin action).
 * @param {string} userId - The ID of the user.
 * @param {string} oldPassword - The user's current password.
 * @param {string} newPassword - The new password.
 * @returns {Promise} The promise from the axios PATCH request.
 */
export const updatePassword = (userId, oldPassword, newPassword) => {
  const payload = {
    oldPassword: oldPassword,
    newPassword: newPassword,
    site: process.env.REACT_APP_SITE_ID,
  };
  return axiosInstance.patch(`/users/update-password/${userId}`, payload);
};
